import { useEffect } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import PortalLayout from '@/components/layout/PortalLayout'
import CallbackPage from '@/pages/auth/CallbackPage'
import SignInPage from '@/pages/auth/SignInPage'
import HomePage from '@/pages/HomePage'
import LoadingSpinner from '@/components/common/LoadingSpinner'

// ALLOWED_RETURN_DOMAIN のサブドメインのみ受け入れ、相対 URL や外部ドメインは拒否する。
function getSafeReturnTo(): string | null {
  if (typeof window === 'undefined') return null
  const raw = new URLSearchParams(window.location.search).get('return_to')
  if (!raw) return null
  try {
    const url = new URL(raw)
    if (url.protocol !== 'https:' && url.protocol !== 'http:') return null
    const allowed = import.meta.env.VITE_ALLOWED_RETURN_DOMAIN
    if (!allowed) return null
    if (url.hostname === allowed || url.hostname.endsWith(`.${allowed}`)) {
      return url.toString()
    }
    return null
  } catch {
    return null
  }
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth()

  // 既にサインイン済みでサブアプリから戻り先指定があればそのまま転送する。
  // (このケースは SignInPage を描画しないので redirect 処理がそこに無い)
  useEffect(() => {
    if (isLoading || !isAuthenticated) return
    const returnTo = getSafeReturnTo()
    if (returnTo) window.location.href = returnTo
  }, [isLoading, isAuthenticated])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (!isAuthenticated) {
    return <SignInPage />
  }

  return <>{children}</>
}

function App() {
  return (
    <Routes>
      <Route path="/callback" element={<CallbackPage />} />

      <Route
        path="/"
        element={
          <ProtectedRoute>
            <PortalLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<HomePage />} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}

export default App
